import { StrictMode, type JSX, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

interface ArtworkFeature {
  title: string;
  artist: string;
  year: string;
  imageUrl: string;
  attribution: string;
}

const featuredArtworks = [
  {
    title: "Water Lilies",
    artist: "Claude Monet",
    year: "1906",
    imageUrl:
      "https://pub-ac3655131b854f40955ca2b43fa5a112.r2.dev/catalog/artic/water-lilies-aic-16568/preview.jpg",
    attribution: "Art Institute of Chicago, CC0 Open Access"
  },
  {
    title: "Under the Wave off Kanagawa",
    artist: "Katsushika Hokusai",
    year: "1831",
    imageUrl: "https://images.metmuseum.org/CRDImages/as/web-large/DP130155.jpg",
    attribution: "The Metropolitan Museum of Art, Open Access"
  },
  {
    title: "The Starry Night",
    artist: "Vincent van Gogh",
    year: "1889",
    imageUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ea/Van_Gogh_-_Starry_Night_-_Google_Art_Project.jpg/1280px-Van_Gogh_-_Starry_Night_-_Google_Art_Project.jpg",
    attribution: "Public domain reproduction via Wikimedia Commons"
  }
] as const satisfies readonly ArtworkFeature[];

const popularArtists = [
  "Monet",
  "Van Gogh",
  "Hokusai",
  "Vermeer",
  "Klimt",
  "O'Keeffe",
  "Hiroshige",
  "Cézanne"
] as const;

const workflowSteps = [
  {
    label: "Search",
    title: "Find the piece you want.",
    body: "Search by artist, era, or mood across a curated, license-clean catalog. No accounts, no queues."
  },
  {
    label: "Prepare",
    title: "Crop, mat, and tune locally.",
    body: "Adjust the 16:9 crop, pick a mat preset, and balance the color — everything renders on device."
  },
  {
    label: "Send",
    title: "Push it to the wall.",
    body: "Direct local upload over Wi-Fi when supported, with SmartThings as a graceful fallback."
  }
] as const;

const manageFeatures = [
  "Build a personal collection",
  "Switch the wall in two taps",
  "16:9 mats matched to Art Mode",
  "Works without an Art Mode subscription"
] as const;

function App(): JSX.Element {
  const pathname = normalizePath(window.location.pathname);

  return (
    <div className="page">
      <SiteHeader />
      {pathname === "/privacy" ? <PrivacyPage /> : <HomePage />}
      <SiteFooter />
    </div>
  );
}

function normalizePath(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, "");
  return normalized.length > 0 ? normalized : "/";
}

function Container({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={`container${className ? ` ${className}` : ""}`}>{children}</div>;
}

function SiteHeader(): JSX.Element {
  return (
    <header className="siteHeader" aria-label="Art Glow site header">
      <Container className="siteHeaderInner">
        <a className="brandLockup" href="/" aria-label="Art Glow home">
          <img src="/art-glow-icon.png" alt="" />
          <span>Art Glow</span>
        </a>
        <nav className="siteNav" aria-label="Primary navigation">
          <a href="/#how-it-works">How it works</a>
          <a href="/#catalog">Catalog</a>
          <a href="/#pricing">Free forever</a>
          <a href="/privacy">Privacy</a>
        </nav>
        <a
          className="headerCta"
          href="mailto:hello@artglow.tv?subject=Art%20Glow%20early%20access"
        >
          Get it free
        </a>
      </Container>
    </header>
  );
}

function HomePage(): JSX.Element {
  const heroArtwork = featuredArtworks[0];

  return (
    <main className="home">
      <section className="hero" id="top" aria-labelledby="hero-title">
        <div className="heroBackdrop" aria-hidden="true">
          <div className="heroBlob heroBlobA" />
          <div className="heroBlob heroBlobB" />
          <div className="heroGrain" />
        </div>
        <Container className="heroInner">
          <div className="heroCopy">
            <span className="pill pillFree">
              <span className="pillDot" /> Free forever · Early access
            </span>
            <h1 id="hero-title">
              The quiet way to <em>curate</em> your TV.
            </h1>
            <p className="heroLead">
              A <strong>free</strong> mobile app for searching a museum-grade
              catalog, preparing each piece on device, and sending it straight to
              your Art Mode TV.
            </p>
            <div className="heroSearch" aria-hidden="true">
              <svg className="heroSearchIcon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.6" />
                <path d="m20 20-3.6-3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <span className="heroSearchText">Search Monet, ukiyo-e, abstract minimal…</span>
              <span className="heroSearchKbd">⌘K</span>
            </div>
            <div className="heroActions">
              <a
                className="primaryAction"
                href="mailto:hello@artglow.tv?subject=Art%20Glow%20early%20access"
              >
                Get it free
              </a>
              <a className="secondaryAction" href="#how-it-works">
                See how it works
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="m6 4 4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                </svg>
              </a>
            </div>
            <ul className="heroPerks" aria-label="What you get with Art Glow">
              <li>
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="m4 8.5 2.5 2.5L12 5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>$0 forever</span>
              </li>
              <li>
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="m4 8.5 2.5 2.5L12 5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>No account</span>
              </li>
              <li>
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="m4 8.5 2.5 2.5L12 5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>No ads, ever</span>
              </li>
            </ul>
          </div>

          <div
            className="heroVisual"
            aria-label={`${heroArtwork.title} preview in a TV frame`}
          >
            <div className="wallGlow" aria-hidden="true" />
            <div className="tvFrame">
              <span className="tvFrameSpec">65" — Art Mode</span>
              <img
                src={heroArtwork.imageUrl}
                alt={`${heroArtwork.title} by ${heroArtwork.artist}`}
              />
            </div>
            <div className="tvCaption">
              <strong>{heroArtwork.title}</strong>
              <span>
                {heroArtwork.artist} · {heroArtwork.year}
              </span>
            </div>
            <div className="heroChipCloud" aria-hidden="true">
              {popularArtists.slice(0, 5).map((name, index) => (
                <span key={name} className={`heroChip heroChip-${index}`}>
                  {name}
                </span>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <section className="processSection" id="how-it-works" aria-labelledby="process-title">
        <Container>
          <div className="sectionIntro">
            <span className="eyebrow">How it works</span>
            <h2 id="process-title">
              Three quiet steps from <em>catalog</em> to <em>canvas</em>.
            </h2>
          </div>
          <div className="processGrid">
            {workflowSteps.map((step, index) => (
              <article key={step.label} className="processCard">
                <span className="processNumber">{String(index + 1).padStart(2, "0")}</span>
                <span className="processLabel">{step.label}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="catalogSection" id="catalog" aria-labelledby="catalog-title">
        <Container>
          <div className="catalogHead">
            <div>
              <span className="eyebrow">Catalog</span>
              <h2 id="catalog-title">
                Curated, license-clean,<br />ready for the wall.
              </h2>
            </div>
            <p className="catalogIntro">
              Every piece is sourced from open-access museum collections — the Art
              Institute of Chicago, the Met, Rijksmuseum, and more. You browse, we
              keep the rights and metadata clean.
            </p>
          </div>

          <div className="artistChips" aria-label="Browse by artist">
            {popularArtists.map((name) => (
              <a key={name} href="#catalog" className="artistChip">
                {name}
              </a>
            ))}
            <a href="#catalog" className="artistChip artistChipMore">
              + 80 more
            </a>
          </div>

          <div className="artGrid" aria-label="Featured artwork">
            {featuredArtworks.map((artwork) => (
              <figure key={artwork.title} className="artCard">
                <div className="artCardMedia">
                  <img
                    src={artwork.imageUrl}
                    alt={`${artwork.title} by ${artwork.artist}`}
                    loading="lazy"
                  />
                </div>
                <figcaption>
                  <div className="artCardTitle">
                    <strong>{artwork.title}</strong>
                    <span>
                      {artwork.artist} · {artwork.year}
                    </span>
                  </div>
                  <span className="artCardBadge">Ready for TV</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </Container>
      </section>

      <section className="manageSection" aria-labelledby="manage-title">
        <Container className="manageInner">
          <div className="manageCopy">
            <span className="eyebrow">On the wall</span>
            <h2 id="manage-title">
              Manage your TV's art<br />without leaving the couch.
            </h2>
            <p>
              Build a small, personal collection on your phone. Tap to swap the
              piece on the wall. Everything stays on your network, and your
              prepared images stay on your device.
            </p>
            <ul className="manageList">
              {manageFeatures.map((feature) => (
                <li key={feature}>
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path
                      d="m4 8.5 2.5 2.5L12 5.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="phoneMock" aria-hidden="true">
            <div className="phoneFrame">
              <div className="phoneNotch" />
              <div className="phoneScreen">
                <div className="phoneHeader">
                  <span>Library</span>
                  <span className="phoneHeaderAction">Send</span>
                </div>
                <div className="phoneSearchRow">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.6" fill="none" />
                    <path d="m20 20-3.6-3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                  <span>Search</span>
                </div>
                <div className="phoneGrid">
                  {featuredArtworks.map((artwork, index) => (
                    <div key={artwork.title} className={`phoneTile phoneTile-${index}`}>
                      <img src={artwork.imageUrl} alt="" loading="lazy" />
                    </div>
                  ))}
                  <div className="phoneTile phoneTilePlaceholder" />
                </div>
                <div className="phoneActiveBar">
                  <span className="phoneActiveDot" />
                  Now on the wall · Living Room TV
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="finalCta" id="pricing" aria-labelledby="cta-title">
        <Container>
          <div className="finalCtaCard">
            <span className="priceTag" aria-hidden="true">
              <span className="priceTagAmount">$0</span>
              <span className="priceTagLabel">Forever</span>
            </span>
            <span className="eyebrow">Free · Coming soon</span>
            <h2 id="cta-title">
              Art Glow is free.<br />And it stays that way.
            </h2>
            <p>
              No subscription, no account, no ads. Art Glow is a small, focused app
              built around a curated, license-clean catalog — join early access and
              we'll let you know the moment it ships.
            </p>
            <div className="finalCtaActions">
              <a
                className="primaryAction"
                href="mailto:hello@artglow.tv?subject=Art%20Glow%20early%20access"
              >
                Get it free
              </a>
            </div>
          </div>
        </Container>
      </section>
    </main>
  );
}

function PrivacyPage(): JSX.Element {
  return (
    <main className="legalPage" aria-labelledby="privacy-title">
      <Container>
        <div className="legalHero">
          <span className="eyebrow">Privacy</span>
          <h1 id="privacy-title">Privacy Policy</h1>
          <p className="legalMeta">Last updated May 26, 2026</p>
        </div>

        <div className="legalGrid">
          <article className="legalCard">
            <h2>What Art Glow Does</h2>
            <p>
              Art Glow loads a public catalog of curated artwork, lets you prepare a
              16:9 image locally, and helps you save it to Photos or send it to a
              compatible TV on the same Wi-Fi network.
            </p>
          </article>

          <article className="legalCard">
            <h2>What We Do Not Collect</h2>
            <p>
              The mobile app does not require an account, does not ask for your name
              or email, and does not upload prepared artwork outputs to the Art Glow
              backend.
            </p>
          </article>

          <article className="legalCard">
            <h2>Local Network</h2>
            <p>
              Local network access is used to find Samsung TVs with Art Mode on your
              Wi-Fi. Direct upload runs locally; if it is unavailable, the app keeps
              the prepared image on your device and offers the SmartThings fallback.
            </p>
          </article>

          <article className="legalCard">
            <h2>Photos</h2>
            <p>
              Photos add-only access is used when you choose to save a prepared
              artwork JPEG. Art Glow does not need to browse your photo library for
              the v1 catalog-only workflow.
            </p>
          </article>

          <article className="legalCard legalCardWide">
            <h2>Operational Logs</h2>
            <p>
              The catalog API and hosting providers may process standard operational
              logs such as request time, endpoint, status code, and coarse network
              metadata needed to operate and secure the service. These logs are not
              used for consumer accounts or cross-app tracking.
            </p>
          </article>
        </div>
      </Container>
    </main>
  );
}

function SiteFooter(): JSX.Element {
  return (
    <footer className="siteFooter">
      <Container className="siteFooterInner">
        <div className="siteFooterBrand">
          <img src="/art-glow-icon.png" alt="" />
          <div>
            <strong>Art Glow</strong>
            <span>Curated art for Art Mode TVs.</span>
          </div>
        </div>
        <nav aria-label="Footer navigation" className="siteFooterNav">
          <a href="/privacy">Privacy</a>
          <a href="mailto:hello@artglow.tv">Contact</a>
        </nav>
        <p className="siteFooterLegal">
          © {new Date().getFullYear()} Art Glow. All artwork shown is reproduced from
          open-access museum collections.
        </p>
      </Container>
    </footer>
  );
}

const root = document.getElementById("root");

if (!root) {
  throw new Error("Root element not found.");
}

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>
);
